/**
 * ChartHeader — единый компонент шапки графика для всех страниц с TaChart.
 *
 * Один источник правды для:
 *   - TF-переключателей (D1 / 1H / 15M / 5M)
 *   - Layer-toggle (S/R, Fib, EMA)
 *
 * CSS-классы префиксованы `ch-` чтобы не конфликтовать со старыми inline-стилями.
 * Стили — в shared `CHART_HEADER_CSS` (web_app.py), подключается через placeholder
 * `<!-- CHART_HEADER_CSS_BLOCK -->` на каждой странице с графиком.
 *
 * Usage:
 *   const header = ChartHeader.build({
 *     container: document.getElementById('myChartHeader'),
 *     prefix: 'panel',                             // unique для id'ов кнопок
 *     tfs: ['D1', '1h', '15m', '5m'],              // или подмножество
 *     layers: ['sr', 'fib', 'ema'],                // или подмножество
 *     defaultTf: 'D1',
 *     defaultLayers: { sr: true, fib: false, ema: true },
 *     leftSlot: '<span class="ch-title">SBER · Сбербанк</span>',
 *     levelsInfo: true,                            // ⓘ слева от слоёв (admin-only)
 *     onTfChange: (tf) => { ... },
 *     onLayerToggle: (layer, isActive) => { ... }
 *   });
 *
 *   header.setTf('1h');         // программно
 *   header.setLayer('fib', true);
 *   header.getTf();             // → 'D1'
 *   header.getLayers();         // → {sr: true, fib: false, ema: true}
 */
(function () {
  'use strict';

  const TF_LABELS = { 'D1': 'D1', '4h': '4H', '1h': '1H', '15m': '15M', '5m': '5M' };
  const LAYER_LABELS = {
    'sr': 'S/R',
    'fib': 'Fib',
    'ema': 'EMA',
    'mtf': 'MTF',           // уровни детектора (D1+W1+density)
    'vsa': 'VSA',           // legacy-слой, в текущих страницах не используется
    'forecast': 'Прогноз',  // в /portfolio modal
  };

  function build(opts) {
    opts = opts || {};
    const container = opts.container;
    if (!container) {
      throw new Error('ChartHeader.build: container is required');
    }
    const prefix = opts.prefix || 'ch';
    const tfs = opts.tfs || ['D1', '1h', '15m', '5m'];
    const layers = opts.layers || ['sr', 'fib', 'ema'];
    const onTfChange = opts.onTfChange || function () {};
    const onLayerToggle = opts.onLayerToggle || function () {};

    let curTf = opts.defaultTf || tfs[0];
    const layerState = Object.assign(
      { sr: false, fib: false, ema: false },
      opts.defaultLayers || {}
    );

    // ── Build HTML ──
    let html = '<div class="ch-header">';
    if (opts.leftSlot) {
      html += '<div class="ch-left">' + opts.leftSlot + '</div>';
    }
    html += '<div class="ch-tools">';

    // Layer toggles (перед ними — опциональный значок справки об уровнях)
    if (layers.length > 0) {
      html += '<div class="ch-layer-toggle" id="' + prefix + '-layers">';
      if (opts.levelsInfo) {
        html += '<button class="ch-info-btn" id="' + prefix + '-levels-info" '
          + 'type="button" aria-label="Как считаются уровни">i</button>';
      }
      layers.forEach(function (lr) {
        const active = layerState[lr] ? ' active' : '';
        const label = LAYER_LABELS[lr] || lr;
        html += '<button class="ch-layer-btn' + active + '" '
          + 'data-layer="' + lr + '" '
          + 'id="' + prefix + '-layer-' + lr + '" '
          + 'type="button">' + label + '</button>';
      });
      html += '</div>';
    }

    // TF switch
    if (tfs.length > 0) {
      html += '<div class="ch-tf-switch" id="' + prefix + '-tfs">';
      tfs.forEach(function (tf) {
        const active = (tf === curTf) ? ' active' : '';
        const label = TF_LABELS[tf] || tf;
        html += '<button class="ch-tf-btn' + active + '" '
          + 'data-tf="' + tf + '" '
          + 'id="' + prefix + '-tf-' + tf + '" '
          + 'type="button">' + label + '</button>';
      });
      html += '</div>';
    }

    html += '</div></div>';
    container.innerHTML = html;

    // ── Wire handlers ──
    container.querySelectorAll('.ch-tf-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const tf = btn.dataset.tf;
        if (tf === curTf) return;
        setTf(tf);
        onTfChange(tf);
      });
    });
    container.querySelectorAll('.ch-layer-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const lr = btn.dataset.layer;
        const next = !layerState[lr];
        setLayer(lr, next);
        onLayerToggle(lr, next);
      });
    });

    // Справка об уровнях: содержимое приходит с сервера (window.__LEVELS_INFO_HTML__),
    // чтобы числа брались из тех же модулей, которыми считаются уровни.
    // Тултип живёт в <body> и позиционируется fixed — .chart-panel имеет
    // overflow:hidden, вложенный absolute обрезался бы по низу панели.
    const infoBtn = container.querySelector('.ch-info-btn');
    if (infoBtn) {
      let tip = null;
      const hide = function () { if (tip) tip.classList.remove('show'); };
      const show = function () {
        const body = window.__LEVELS_INFO_HTML__;
        if (!body) return;
        if (!tip) {
          tip = document.createElement('div');
          tip.className = 'ch-info-tip';
          document.body.appendChild(tip);
        }
        tip.innerHTML = body;
        tip.classList.add('show');
        // Прижимаем к кнопке, но не даём вылезти за края вьюпорта.
        const r = infoBtn.getBoundingClientRect();
        const w = tip.offsetWidth, h = tip.offsetHeight;
        let left = r.left, top = r.bottom + 6;
        if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
        if (left < 8) left = 8;
        if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6);
        tip.style.left = left + 'px';
        tip.style.top = top + 'px';
      };
      infoBtn.addEventListener('mouseenter', show);
      infoBtn.addEventListener('mouseleave', hide);
      infoBtn.addEventListener('click', function (e) {   // на тач-устройствах
        e.preventDefault();
        if (tip && tip.classList.contains('show')) hide(); else show();
      });
      window.addEventListener('scroll', hide, true);
    }

    // ── Public API ──
    function setTf(tf) {
      if (!tfs.includes(tf)) return;
      curTf = tf;
      container.querySelectorAll('.ch-tf-btn').forEach(function (b) {
        b.classList.toggle('active', b.dataset.tf === tf);
      });
    }
    function setLayer(lr, on) {
      layerState[lr] = !!on;
      const btn = container.querySelector('.ch-layer-btn[data-layer="' + lr + '"]');
      if (btn) btn.classList.toggle('active', !!on);
    }
    function getTf() { return curTf; }
    function getLayers() { return Object.assign({}, layerState); }

    return { setTf: setTf, setLayer: setLayer, getTf: getTf, getLayers: getLayers };
  }

  window.ChartHeader = { build: build };
})();
